import { PrismicRichText } from '@prismicio/react';
import DynamicForm from '@remote-com/mushu/src/components/Form/DynamicForm';
import {
  SuccessTitle,
  SuccessDescription,
  SuccessActions,
} from '@remote-com/mushu/src/components/Success';
import SuccessMark from '@remote-com/mushu/src/components/Success/SuccessMark';
import { GlobalStyles, theme as mushuTheme } from '@remote-com/mushu/src/config/styles';
import { useFormEvents } from '@remote-com/mushu/src/hooks/useFormEvents';
import { ButtonInline, Button } from '@remote-com/norma/core/button';
import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { dataLayerPush } from 'remote-com-shared/components/CookieScripts/helpers';
import { Modal } from 'remote-com-shared/components/ModalForm';
import {
  getCurrentURL,
  registerLead,
} from 'remote-com-shared/components/ModalForm/helpers/services';
import {
  FormContainer,
  SubmitButton,
  CaptchaWrapper,
  SubsribeSuccessContainer,
  ModalTitle,
  ModalDescription,
  FormError,
  DynamicFormWrapper,
  SubmitButtonWrapper,
} from 'remote-com-shared/components/ModalForm/styles';
import { getFields } from 'remote-com-shared/domains/hubspot/fields';
import { useHubspotForm } from 'remote-com-shared/domains/hubspot/hooks';
import { LocaleContextConsumer } from 'remote-com-shared/domains/locales/context';
import { ThemeProvider } from 'styled-components';

function ModalForm({ onCancel, formId, formTitle, formDescription }) {
  const [showLoader, setShowLoader] = useState(false);
  const [showFormError, setShowFormError] = useState(false);
  const [isCaptchaDone, setIsCaptchaDone] = useState(false);
  const [showSuccessState, setShowSuccessState] = useState(false);

  const {
    isLoading,
    submitText = '',
    inlineMessage: successMessage = '',
    formFieldGroups = [],
    submitUrl,
  } = useHubspotForm(formId);

  const fields = getFields(formFieldGroups);

  const { formState } = useFormEvents();
  const [formValues, setFormValues] = useState({});

  const reCaptchaRef = useRef(null);

  async function handleSubmit(values, { setSubmitting }) {
    const updatedFormValues = {
      ...formValues,
      ...values,
    };
    setFormValues(updatedFormValues);

    setShowLoader(true);

    if (isCaptchaDone) {
      try {
        await registerLead(
          {
            ...values,
            ...(Object.prototype.hasOwnProperty.call(values, 'utm')
              ? {
                  utm: getCurrentURL(),
                }
              : {}),
          },
          submitUrl
        );

        dataLayerPush({ event: 'user-submitted-hubspot-form', label: window.location.pathname });

        setShowLoader(false);
        setShowSuccessState(true);
      } catch (exception) {
        reCaptchaRef.current.reset();
        setIsCaptchaDone(false);
        setShowFormError(true);
        setShowLoader(false);
        setSubmitting(false);
      }
    }
  }

  const handleCaptchaChange = (value) => {
    setIsCaptchaDone(value);
  };

  return (
    <ThemeProvider theme={{ mode: 'light', ...mushuTheme }}>
      <GlobalStyles />

      <FormContainer>
        {showSuccessState ? (
          <SubsribeSuccessContainer>
            <SuccessMark />
            <SuccessTitle>Thank you!</SuccessTitle>
            <SuccessDescription dangerouslySetInnerHTML={{ __html: successMessage }} />
            <SuccessActions>
              <SubmitButton onClick={() => onCancel()}>Close</SubmitButton>
            </SuccessActions>
          </SubsribeSuccessContainer>
        ) : (
          <DynamicFormWrapper>
            <ModalTitle>{formTitle}</ModalTitle>
            <ModalDescription as="div">
              <PrismicRichText field={formDescription} />
            </ModalDescription>
            {showFormError && (
              <FormError>
                Something went wrong. Please try again, or contact{' '}
                <ButtonInline href="mailto:help@remote.com">help@remote.com</ButtonInline> if this
                error persists. Thank you.
              </FormError>
            )}

            {isLoading ? (
              <SubmitButtonWrapper>
                <SubmitButton isLoading={isLoading}>Loading</SubmitButton>
              </SubmitButtonWrapper>
            ) : (
              <>
                <DynamicForm
                  formName="partner-request-demo"
                  fields={fields}
                  fieldValues={formValues}
                  formError={formState.errorMessage}
                  formTheme="dark"
                  formSize="small"
                  handleOnSubmit={handleSubmit}
                  isLoading={formState.isLoading}
                  enableReinitialize
                />

                <CaptchaWrapper>
                  <LocaleContextConsumer>
                    {(locale) => (
                      <ReCAPTCHA
                        hl={locale}
                        ref={reCaptchaRef}
                        sitekey={process.env.NEXT_PUBLIC_CAPTCHA_KEY}
                        onChange={handleCaptchaChange}
                      />
                    )}
                  </LocaleContextConsumer>
                </CaptchaWrapper>
                <SubmitButtonWrapper>
                  <SubmitButton
                    isLoading={showLoader}
                    disabled={!isCaptchaDone}
                    form="partner-request-demo"
                  >
                    {submitText}
                  </SubmitButton>
                </SubmitButtonWrapper>
              </>
            )}
          </DynamicFormWrapper>
        )}
      </FormContainer>
    </ThemeProvider>
  );
}

function FormModal({ onCancel, visible, ...props }) {
  return (
    <Modal {...{ visible, onCancel }} width="620px" footer={null} bodyStyle={{ padding: '0px' }}>
      <ModalForm {...{ onCancel, ...props }} />
    </Modal>
  );
}

export function OverviewSectionFormButton({
  children,
  formId,
  formTitle,
  formDescription,
  formEventName,
}) {
  const [isOpen, setIsOpen] = useState(false);

  function openModal() {
    setIsOpen(true);
  }
  function closeModal() {
    setIsOpen(false);
  }

  return (
    <>
      <Button type="button" forwardAs="button" onClick={openModal} $shadow>
        {children}
      </Button>
      <FormModal
        visible={isOpen}
        onCancel={closeModal}
        {...{ formId, formTitle, formDescription, formEventName }}
      />
    </>
  );
}
